import { useEffect, useMemo, useState } from "react";
import {
  FLAVOUR_GUEST_LABELS,
  flavourColour,
  flavourNameKey,
  flavourRefsFor,
  MAX_PREFERRED_FAMILIES,
  money,
  NO_ANSWERS,
  resolveFlavour,
  SERVE_LABEL,
  SPIRIT_LABEL,
  STRENGTH_LABEL,
  WHATTAIL_TASTES,
  whatTailMenu,
  whatTailOptions,
  whatTailSuggest,
  type FlavourProfile,
  type WhatTailAnswers,
} from "@baros/core";
import { useApp } from "../context.js";
import type { ScreenProps } from "../screens.js";
import { flavourApi } from "../wheel/flavour-api.js";
import "./whattail.css";

/**
 * Screen 18 — WhatTail. A bartender asks a guest a few questions and lands
 * on three drinks from tonight's list.
 *
 * Everything is worked out here on the tablet from the library, the venue's
 * prices and the flavour database's profiles: no language model, no wait and
 * no spend. The scoring and the order are `@baros/core`'s `whattail.ts`. The
 * order is the bar's margin, and nothing on this screen says so — a guest
 * may be looking at it.
 */

type StepId = "families" | "tastes" | "strength" | "serve" | "spirits" | "avoid";

const STEP_TITLE: Readonly<Record<StepId, string>> = {
  families: "What flavours do they go for?",
  tastes: "Sweet, sour or bitter?",
  strength: "How strong?",
  serve: "How do they like it served?",
  spirits: "A spirit they love?",
  avoid: "Anything they won't drink?",
};

const STEP_HINT: Readonly<Record<StepId, string>> = {
  families: `Up to ${MAX_PREFERRED_FAMILIES}. It moves on when you stop tapping.`,
  tastes: "Any that apply. It moves on when you stop tapping.",
  strength: "Tap one.",
  serve: "Tap one.",
  spirits: "Any they'd like it made on.",
  avoid: "These are ruled out completely.",
};

/** What the "no preference" chip says on each question. */
const STEP_NONE: Readonly<Record<StepId, string>> = {
  families: "Don't mind",
  tastes: "Don't mind",
  strength: "Don't mind",
  serve: "Don't mind",
  spirits: "No favourite",
  avoid: "Nothing",
};

/** How many answers a question takes before there is nothing left to wait for. */
const STEP_CAP: Readonly<Record<StepId, number>> = {
  families: MAX_PREFERRED_FAMILIES,
  tastes: Infinity,
  strength: 1,
  serve: 1,
  spirits: Infinity,
  avoid: Infinity,
};

/**
 * There is no Next button (Rob, 2026-10-12): a tap answers the question and
 * the screen moves on by itself. A question with one answer moves on at
 * once — just long enough to see the tap land. One that takes several waits
 * for a pause, so a second and third can be tapped; each tap starts the
 * wait again.
 */
const ADVANCE_NOW_MS = 220;
const ADVANCE_AFTER_PAUSE_MS = 1300;

function answered(a: WhatTailAnswers, step: StepId): number {
  switch (step) {
    case "families":
      return a.families.length;
    case "tastes":
      return a.tastes.length;
    case "strength":
      return a.strength ? 1 : 0;
    case "serve":
      return a.serve ? 1 : 0;
    case "spirits":
      return a.spirits.length;
    case "avoid":
      return a.avoid.length;
  }
}

function cleared(a: WhatTailAnswers, step: StepId): WhatTailAnswers {
  switch (step) {
    case "families":
      return { ...a, families: [] };
    case "tastes":
      return { ...a, tastes: [] };
    case "strength":
      return { ...a, strength: null };
    case "serve":
      return { ...a, serve: null };
    case "spirits":
      return { ...a, spirits: [] };
    case "avoid":
      return { ...a, avoid: [] };
  }
}

function toggled<T>(list: readonly T[], value: T, cap = Infinity): T[] {
  if (list.includes(value)) return list.filter((v) => v !== value);
  return list.length >= cap ? [...list] : [...list, value];
}

export function WhatTailScreen(_props: ScreenProps) {
  const app = useApp();
  const { token, venueId } = app;

  const ingredientsById = useMemo(
    () => new Map(app.library.ingredients.map((i) => [i.ingredientId, i])),
    [app.library.ingredients],
  );
  const specsById = useMemo(() => new Map(app.library.specs.map((s) => [s.specId, s])), [app.library.specs]);

  // The flavour database's profiles for everything in the library's builds.
  // Fetched once; the questions work without them, on each ingredient's
  // primary family, so a failure here only costs the taste question.
  const [profiles, setProfiles] = useState<ReadonlyMap<string, FlavourProfile> | null>(null);
  useEffect(() => {
    let live = true;
    const refs = flavourRefsFor([...ingredientsById.keys()], ingredientsById, specsById);
    flavourApi
      .profiles(token, refs)
      .then((found) => {
        if (live) setProfiles(new Map(found.map((p) => [flavourNameKey(p.name), p])));
      })
      .catch(() => {
        if (live) setProfiles(new Map());
      });
    return () => {
      live = false;
    };
  }, [token, ingredientsById, specsById]);

  const drinks = useMemo(() => {
    const profilesByIngredientId = new Map<string, FlavourProfile>();
    if (profiles && profiles.size > 0) {
      for (const id of ingredientsById.keys()) {
        const resolved = resolveFlavour(id, ingredientsById, specsById, profiles);
        if (resolved) profilesByIngredientId.set(id, resolved.profile);
      }
    }
    return whatTailMenu({
      specs: app.library.specs,
      ingredientsById,
      overridesBySpecId: app.overrides,
      profilesByIngredientId,
    });
  }, [app.library.specs, app.overrides, ingredientsById, specsById, profiles]);

  const options = useMemo(() => whatTailOptions(drinks), [drinks]);
  const steps = useMemo<StepId[]>(() => {
    const out: StepId[] = [];
    if (options.families.length > 0) out.push("families");
    if (options.tastes) out.push("tastes");
    if (options.strengths.length > 1) out.push("strength");
    if (options.serves.length > 1) out.push("serve");
    if (options.spirits.length > 1) out.push("spirits", "avoid");
    return out;
  }, [options]);

  const [answers, setAnswers] = useState<WhatTailAnswers>(NO_ANSWERS);
  const [at, setAt] = useState(0);
  // The last tap on a choice: which question it was on, and a count so two
  // taps on the same question are two events.
  const [tap, setTap] = useState<{ readonly at: number; readonly n: number } | null>(null);
  const done = at >= steps.length;
  const step = steps[at];

  const result = useMemo(() => whatTailSuggest(drinks, answers), [drinks, answers]);

  // Move on after a tap. Only a tap starts this: coming back to a question
  // that already has an answer leaves it sitting there to be changed.
  const waitMs =
    tap && step && tap.at === at
      ? answered(answers, step) === 0
        ? null
        : answered(answers, step) >= STEP_CAP[step]
          ? ADVANCE_NOW_MS
          : ADVANCE_AFTER_PAUSE_MS
      : null;
  useEffect(() => {
    if (waitMs === null || !tap) return;
    const from = tap.at;
    const timer = window.setTimeout(() => setAt((now) => (now === from ? now + 1 : now)), waitMs);
    return () => window.clearTimeout(timer);
  }, [waitMs, tap]);

  if (!venueId) {
    return (
      <div className="pad">
        <div className="msg err">You are not assigned to a venue, so there is no list to suggest from.</div>
      </div>
    );
  }

  const restart = () => {
    setAnswers(NO_ANSWERS);
    setAt(0);
    setTap(null);
  };
  const goTo = (index: number) => {
    setTap(null);
    setAt(index);
  };

  const chip = (key: string, label: string, on: boolean, press: () => void, colour?: string, disabled = false) => (
    <button
      key={key}
      type="button"
      className="wt-chip"
      aria-pressed={on}
      disabled={disabled}
      style={colour ? { ["--c" as string]: colour } : undefined}
      onClick={() => {
        press();
        setTap((t) => ({ at, n: (t?.n ?? 0) + 1 }));
      }}
    >
      {colour && <span className="dot" />}
      {label}
    </button>
  );

  const choices = (id: StepId) => {
    switch (id) {
      case "families":
        return options.families.map((f) =>
          chip(
            f,
            FLAVOUR_GUEST_LABELS[f],
            answers.families.includes(f),
            () => setAnswers((a) => ({ ...a, families: toggled(a.families, f, MAX_PREFERRED_FAMILIES) })),
            flavourColour(f),
            !answers.families.includes(f) && answers.families.length >= MAX_PREFERRED_FAMILIES,
          ),
        );
      case "tastes":
        return WHATTAIL_TASTES.map((t) =>
          chip(t, t, answers.tastes.includes(t), () => setAnswers((a) => ({ ...a, tastes: toggled(a.tastes, t) }))),
        );
      case "strength":
        return options.strengths.map((s) =>
          chip(s, STRENGTH_LABEL[s], answers.strength === s, () =>
            setAnswers((a) => ({ ...a, strength: a.strength === s ? null : s })),
          ),
        );
      case "serve":
        return options.serves.map((s) =>
          chip(s, SERVE_LABEL[s], answers.serve === s, () => setAnswers((a) => ({ ...a, serve: a.serve === s ? null : s }))),
        );
      case "spirits":
        return options.spirits.map((s) =>
          chip(s, SPIRIT_LABEL[s], answers.spirits.includes(s), () =>
            // Loving a spirit and refusing it cannot both be true.
            setAnswers((a) => ({ ...a, spirits: toggled(a.spirits, s), avoid: a.avoid.filter((x) => x !== s) })),
          ),
        );
      case "avoid":
        return options.spirits.map((s) =>
          chip(s, SPIRIT_LABEL[s], answers.avoid.includes(s), () =>
            setAnswers((a) => ({ ...a, avoid: toggled(a.avoid, s), spirits: a.spirits.filter((x) => x !== s) })),
          ),
        );
    }
  };

  return (
    <div className="screen wt">
      <div className="row screen-head" style={{ padding: "18px 20px 12px", alignItems: "flex-end", gap: 14 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4 }}>
            {done ? "Offer these" : `Question ${at + 1} of ${steps.length}`}
          </div>
        </div>
        <span className="spacer" />
        {(at > 0 || answers !== NO_ANSWERS) && (
          <button className="btn" onClick={restart}>
            Start again
          </button>
        )}
      </div>

      <div className="wt-body">
        {drinks.length === 0 ? (
          <div className="q-empty">
            Nothing is priced and available at this venue tonight, so there is nothing to suggest. Set prices on the
            specs first.
          </div>
        ) : !done && step ? (
          <>
            <div className="wt-progress" aria-hidden="true">
              {steps.map((s, i) => (
                <span key={s} data-state={i < at ? "done" : i === at ? "now" : "todo"} />
              ))}
            </div>
            <h2 className="wt-question">{STEP_TITLE[step]}</h2>
            <div className="label wt-hint">{STEP_HINT[step]}</div>
            <div className="wt-chips" role="group" aria-label={STEP_TITLE[step]}>
              {choices(step)}
              <button
                type="button"
                className="wt-chip none"
                onClick={() => {
                  setAnswers((a) => cleared(a, step));
                  goTo(at + 1);
                }}
              >
                {STEP_NONE[step]}
              </button>
            </div>
            {/* The pause before a several-answer question moves on, drawn so it is not a surprise. */}
            <div className="wt-wait" aria-hidden="true">
              {waitMs === ADVANCE_AFTER_PAUSE_MS && tap && (
                <span key={tap.n} style={{ animationDuration: `${ADVANCE_AFTER_PAUSE_MS}ms` }} />
              )}
            </div>
            <div className="wt-actions">
              <button className="btn" disabled={at === 0} onClick={() => goTo(at - 1)}>
                Back
              </button>
              <span className="spacer" />
              <button className="btn" onClick={() => goTo(steps.length)}>
                Show drinks now
              </button>
            </div>
            <div className="label wt-foot">{drinks.length} drinks on tonight&rsquo;s list</div>
          </>
        ) : (
          <>
            {result.suggestions.length === 0 ? (
              <div className="q-empty">
                Nothing on tonight&rsquo;s list fits all of that. Go back and loosen an answer, or start again.
              </div>
            ) : (
              <ol className="wt-results">
                {result.suggestions.map((s) => (
                  <li key={s.drink.specId} className="wt-card">
                    <span className="wt-rank">{s.rank}</span>
                    <div className="wt-card-main">
                      <div className="wt-name">{s.drink.name}</div>
                      <div className="wt-why">
                        {s.reasons.length > 0
                          ? s.reasons.map((r) => (
                              <span key={r} className="wt-reason">
                                {r}
                              </span>
                            ))
                          : <span className="label">A house pick</span>}
                      </div>
                      <div className="label wt-facts">
                        {[
                          STRENGTH_LABEL[s.drink.strength],
                          s.drink.serve ? SERVE_LABEL[s.drink.serve] : s.drink.glass,
                          money(s.drink.price),
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    </div>
                    <button
                      className="btn"
                      onClick={() => app.go({ kind: "recipes", params: { specId: s.drink.specId } })}
                    >
                      Open spec
                    </button>
                  </li>
                ))}
              </ol>
            )}
            <div className="wt-actions">
              <button className="btn" disabled={steps.length === 0} onClick={() => goTo(Math.max(0, steps.length - 1))}>
                Back to the questions
              </button>
              <span className="spacer" />
              <button className="btn primary" onClick={restart}>
                Next guest
              </button>
            </div>
            <div className="label wt-foot">Chosen from {result.considered} drinks on tonight&rsquo;s list</div>
          </>
        )}
      </div>
    </div>
  );
}
